/**
 * Optional emphasis and supporting prose. Either can appear independently.
 * Callers supply the content; this view only owns its typography.
 */
import { useContext, type FC, type ReactNode } from 'react';
import { cn } from '@/app/lib/utils';
import { SurfaceHeadingLevel } from './heading-context.ts';

export interface SurfaceHeaderProps {
  crumb?: ReactNode;
  title: string;
  subtitle?: string;
}

export const SurfaceHeader: FC<SurfaceHeaderProps> = ({ crumb, title, subtitle }) => {
  const level = useContext(SurfaceHeadingLevel);
  const Heading = level === 1 ? 'h1' : 'h2';
  if (!crumb && !title && !subtitle) return null;
  return (
    <div className="w-full">
      {crumb ? (
        <div className="mt-4.5 mb-2.5 flex w-full items-center gap-1.75 text-xs text-muted-foreground-subtle animate-fadeUp [&_b]:font-semibold [&_b]:text-muted-foreground">
          {crumb}
        </div>
      ) : null}
      {title ? (
        <Heading
          className={cn(
            'mt-0 w-full break-words text-balance font-semibold leading-tight tracking-tight',
            level === 1 ? 'text-3xl' : 'text-xl',
            subtitle ? 'mb-2' : 'mb-4',
          )}
        >
          {title}
        </Heading>
      ) : null}
      {subtitle ? (
        <p className="mt-0 mb-4 w-full break-words text-pretty text-base leading-relaxed text-muted-foreground">
          {subtitle}
        </p>
      ) : null}
    </div>
  );
};
